import Adjust from '@adjustcom/adjust-web-sdk';

class AdjustService {
  static #instance = null;
  #isReady = false;
  #queue = [];
  #attribution = null;
  #attributionListeners = [];

  constructor() {
    if (AdjustService.#instance) {
      throw new Error(
        '[AdjustService] Use AdjustService.getInstance() instead of new AdjustService()',
      );
    }
    AdjustService.#instance = this;
  }

  static getInstance() {
    if (!AdjustService.#instance) {
      new AdjustService();
    }
    return AdjustService.#instance;
  }

  init(appToken, environment = 'sandbox') {
    if (!appToken) {
      console.error('[AdjustService] No app token provided!');
      return;
    }

    Adjust.initSdk({
      appToken,
      environment,
      logLevel: environment === 'production' ? 'error' : 'verbose',

    });
    this.#isReady = true;
    this.#flushQueue();
    Adjust.waitForAttribution()
      .then((attribution) => {
        this.#attribution = attribution;
        this.#attributionListeners.forEach((cb) => cb(attribution));
      })
      .catch((error) => {
        console.warn('[AdjustService] waitForAttribution failed:', error);
      });
  }

  #flushQueue() {

    while (this.#queue.length > 0) {
      const { eventToken, params } = this.#queue.shift();
      this.#dispatch(eventToken, params);
    }
  }

  async getAttributionCampaignId(timeoutMs = 1500) {
    try {
      if (this.#attribution) return this.#attribution?.network ?? null;
      const attribution = await Promise.race([
        Adjust.waitForAttribution(),
        new Promise((resolve) => setTimeout(() => resolve(null), timeoutMs)),
      ]);
      console.log("attribution", attribution)
      return attribution?.network ?? null;
    } catch (error) {
      console.warn('[AdjustService] getAttributionCampaignId failed:', error);
      return null;
    }
  }

  onAttributionChange(callback) {
    if (typeof callback !== 'function') return;
    this.#attributionListeners.push(callback);
    // fire immediately if we already have it, so late subscribers don't miss it
    if (this.#attribution) callback(this.#attribution);
  }

  trackEvent(eventToken, params = {}) {
    if (!eventToken) {
      console.warn(
        '[AdjustService] trackEvent called without an eventToken — skipping',
      );
      return;
    }

    if (!this.#isReady) {
      this.#queue.push({ eventToken, params });
      return;
    }

    this.#dispatch(eventToken, params);
  }

  #dispatch(eventToken, params = {}) {
    try {
      Adjust.trackEvent({
        eventToken,
        ...(params.revenue != null &&
          params.currency != null && {
          revenue: params.revenue,
          currency: params.currency,
        }),
        ...(params.deduplicationId != null && {
          deduplicationId: String(params.deduplicationId),
        }),
        ...(Array.isArray(params.callbackParams) &&
          params.callbackParams.length > 0 && {
          callbackParams: params.callbackParams,
        }),
        ...(Array.isArray(params.partnerParams) &&
          params.partnerParams.length > 0 && {
          partnerParams: params.partnerParams,
        }),
      });
    } catch (error) {
      console.warn('[AdjustService] trackEvent failed:', error);
    }
  }

  setUserId(userId) {
    if (!userId) return;
    Adjust.addGlobalCallbackParameters([
      { key: 'user_id', value: String(userId) },
    ]);
  }

  clearUserId() {
    Adjust.removeGlobalCallbackParameter('user_id');
  }

  async getWebUUID() {
    try {
      const webUuid = await Adjust.waitForWebUUID();
      return webUuid;
    } catch (error) {
      console.warn('[AdjustService] getWebUUID failed:', error);
      return null;
    }
  }
}

export default AdjustService;
